import { useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDataProvider, useTranslate, type Identifier } from "ra-core";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import type { CrmDataProvider } from "../providers/types";
import {
  buildUnavailabilityRange,
  type UnavailabilityError,
} from "./guardiasRules";
import {
  FIELD_CLASS,
  GUARDIAS_KEY,
  HELP_CLASS,
  QueryBoundary,
  SectionCard,
  formatInstant,
  useGuardiasSave,
  type SectionAdvisor,
} from "./guardiasShared";

type UnavailabilityProps = {
  /** Editors manage every advisor of the project; an asesora only herself. */
  canEdit: boolean;
  advisors: SectionAdvisor[];
  ownId: Identifier | undefined;
};

/**
 * "No disponible" with a start and an end (date + time): set by the advisor herself or, for an
 * advisor of her project, by the Team Leader; listed with a way to cancel it. Cancelling sets
 * `cancelled_at`; nothing is deleted.
 */
export const GuardiasUnavailability = ({
  canEdit,
  advisors,
  ownId,
}: UnavailabilityProps) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { saving, run } = useGuardiasSave();
  const [chosenAdvisor, setChosenAdvisor] = useState("");
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("09:00");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("18:00");
  const [error, setError] = useState<UnavailabilityError | null>(null);

  // An asesora only requests and edits her own ranges.
  const scopeId = canEdit ? undefined : ownId;
  const ranges = useQuery({
    queryKey: [GUARDIAS_KEY, "unavailability", scopeId ?? "project"],
    enabled: canEdit || ownId != null,
    queryFn: () => dataProvider.getAdvisorUnavailability(scopeId),
  });

  const selectable = canEdit
    ? advisors
    : advisors.filter((advisor) => String(advisor.id) === String(ownId));
  const salesId =
    selectable.find((advisor) => String(advisor.id) === chosenAdvisor)?.id ??
    selectable[0]?.id;
  const nameOf = (id: Identifier) =>
    advisors.find((advisor) => String(advisor.id) === String(id))?.name;

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (salesId == null || ownId == null) return;
    const range = buildUnavailabilityRange({
      startDate,
      startTime,
      endDate,
      endTime,
    });
    setError(range.ok ? null : range.error);
    if (!range.ok) return;
    await run(() =>
      dataProvider.create("advisor_unavailability", {
        data: {
          sales_id: salesId,
          created_by: ownId,
          starts_at: range.starts_at,
          ends_at: range.ends_at,
        },
      }),
    );
  };

  return (
    <SectionCard
      title={translate("resources.guardias.unavailable_title")}
      help={translate("resources.guardias.unavailable_help")}
    >
      <QueryBoundary query={ranges}>
        {(rows) => {
          // Only the advisors of this project, whatever else the role can read.
          const visible = rows.filter((row) => nameOf(row.sales_id) != null);
          return visible.length === 0 ? (
            <p className={HELP_CLASS}>
              {translate("resources.guardias.unavailable_empty")}
            </p>
          ) : (
            <ul className="divide-y" data-testid="guardias-unavailability">
              {visible.map((row) => {
                const name = nameOf(row.sales_id) ?? "";
                return (
                  <li
                    key={row.id}
                    className="flex min-h-11 items-center justify-between gap-3 py-2"
                  >
                    <span className="min-w-0 text-base tabular-nums">
                      {canEdit ? `${name} · ` : ""}
                      {translate("resources.guardias.unavailable_range", {
                        start: formatInstant(row.starts_at),
                        end: formatInstant(row.ends_at),
                      })}
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      className="h-11 shrink-0"
                      disabled={saving}
                      aria-label={translate(
                        "resources.guardias.unavailable_cancel_aria",
                        { name },
                      )}
                      onClick={() =>
                        run(() =>
                          dataProvider.update("advisor_unavailability", {
                            id: row.id,
                            data: { cancelled_at: new Date().toISOString() },
                            previousData: {
                              id: row.id,
                              sales_id: row.sales_id,
                            },
                          }),
                        )
                      }
                    >
                      {translate("resources.guardias.unavailable_cancel")}
                    </Button>
                  </li>
                );
              })}
            </ul>
          );
        }}
      </QueryBoundary>

      {selectable.length > 0 ? (
        <form
          className="space-y-3 border-t pt-3"
          onSubmit={onSubmit}
          noValidate
        >
          {canEdit ? (
            <div className="space-y-2">
              <Label htmlFor="unavailable-advisor">
                {translate("resources.guardias.unavailable_advisor")}
              </Label>
              <select
                id="unavailable-advisor"
                className={FIELD_CLASS}
                value={salesId == null ? "" : String(salesId)}
                onChange={(e) => setChosenAdvisor(e.target.value)}
              >
                {selectable.map((advisor) => (
                  <option key={advisor.id} value={String(advisor.id)}>
                    {advisor.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="unavailable-start-date">
                {translate("resources.guardias.unavailable_start_date")}
              </Label>
              <Input
                id="unavailable-start-date"
                type="date"
                className="h-11 text-base"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="unavailable-start-time">
                {translate("resources.guardias.unavailable_start_time")}
              </Label>
              <Input
                id="unavailable-start-time"
                type="time"
                className="h-11 text-base"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="unavailable-end-date">
                {translate("resources.guardias.unavailable_end_date")}
              </Label>
              <Input
                id="unavailable-end-date"
                type="date"
                className="h-11 text-base"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="unavailable-end-time">
                {translate("resources.guardias.unavailable_end_time")}
              </Label>
              <Input
                id="unavailable-end-time"
                type="time"
                className="h-11 text-base"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </div>
          </div>
          {error ? (
            <p role="alert" className="text-[15px] text-destructive">
              {translate(
                error === "required"
                  ? "resources.guardias.unavailable_error_required"
                  : "resources.guardias.unavailable_error_order",
              )}
            </p>
          ) : null}
          <Button type="submit" className="h-11" disabled={saving}>
            {translate("resources.guardias.unavailable_add")}
          </Button>
        </form>
      ) : null}
    </SectionCard>
  );
};
